<script setup lang="ts">
import {Contest} from "@/contest/interfaces";
import useSwr from "@/core/composables/swr/useSwr";
import {contestApi} from "@/container";
import {Skeleton} from "@/core/components/ui/skeleton";
import {storeToRefs} from "pinia";
import {useSystemStore} from "@/stores/system.store";
import ContestPerformerResult from "./ContestPerformerResult.vue";
import {computed, ref, watch} from "vue";
import ContestObjectiveProgress from "@/contest/components/ContestObjectiveProgress.vue";
import {DialogContent, Dialog, DialogTrigger, DialogTitle} from "@/core/components/ui/dialog";

const {environment} = storeToRefs(useSystemStore());
const props = withDefaults(defineProps<{
    contest: Contest;
    shouldLoad?: boolean;
}>(), {
    shouldLoad: true
});
const limit = ref<number>(5);
const open = ref(false);

const swrKey = computed(() => props.shouldLoad ? `contests/${props.contest.id}/top-performers?limit=${limit.value}` : null);

const {data, isLoading, isValidating} = useSwr(
    swrKey,
    () => contestApi().performers(props.contest.id, {limit: limit.value})
);

watch(() => open.value, (newValue) => {
    if (newValue) {
        limit.value = 100;
    }
});

const healthcenterIsDisplayed = (healthcenterId: number) => {
    return data.value?.data.some(result => result.performer.type === 'healthcenter' && result.performer.id === healthcenterId);
};
</script>

<template>
  <div v-if="isLoading" class="mt-3 space-y-3">
    <Skeleton v-for="i in 5" :key="i" class="w-full h-8" />
  </div>
  <div v-else-if="data" class="mt-3 space-y-3">
    <ContestObjectiveProgress v-if="data.objective_progress" :progress="data.objective_progress" />
    <ContestPerformerResult
      v-for="(result) in data.data.slice(0, 5)"
      :key="result.performer.id"
      :result="result"
    />
    <ContestPerformerResult
      v-if="environment?.entityId && !healthcenterIsDisplayed(environment.entityId) && data.current_healthcenter_ranking"
      :result="data.current_healthcenter_ranking"
      :is-current-healthcenter="true"
    />
    <div v-if="data.data.length" class="flex justify-end">
      <Dialog v-model:open="open">
        <DialogTrigger>
          <p class="text-primary font-semibold cursor-pointer">
            Voir plus
          </p>
        </DialogTrigger>
        <DialogContent class="max-h-96 overflow-y-auto">
          <DialogTitle>
            Classement challenge
          </DialogTitle>
          <div v-if="isValidating" class="mt-3 space-y-3">
            <Skeleton v-for="i in 5" :key="i" class="w-full h-8" />
          </div>
          <div v-else class="mt-3 space-y-3">
            <ContestPerformerResult
              v-for="(result) in data.data"
              :key="result.performer.id"
              :result="result"
            />
            <ContestPerformerResult
              v-if="environment?.entityId && !healthcenterIsDisplayed(environment.entityId) && data.current_healthcenter_ranking"
              :result="data.current_healthcenter_ranking"
              :is-current-healthcenter="true"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  </div>
</template>

<style scoped>

</style>
